import { debounce } from './utils.js';

/*
 * plancAddToCart
 * Centralized add-to-cart AJAX wrapper used by dynamic product grids and widgets.
 * - options: { productId, quantity = 1, $button = null, optimistic = true }
 * - Returns a Promise that resolves with the AJAX response (jQuery-style response object)
 * - Attaches to `window.plancAddToCart` for legacy callers
 */
export function plancAddToCart(options = {}) {
  const { productId, quantity = 1, $button = null, optimistic = true } = options;
  const $ = window.jQuery || window.$;

  return new Promise((resolve, reject) => {
    if (!productId) return reject(new Error('productId required'));
    if (typeof $ === 'undefined') return reject(new Error('jQuery required'));

    const ajaxCfg = window.planc_products_ajax || window.planc || {};
    const ajax_url = ajaxCfg.ajax_url || (window.location.origin + '/wp-admin/admin-ajax.php');
    const nonce = ajaxCfg.nonce || (window.planc && window.planc.nonce) || null;

    try {
      if ($button && $button.data('planc_adding')) {
        if (window.PLANC_DEBUG && console) console.debug('plancAddToCart: already in progress for this button');
        return reject(new Error('in-progress'));
      }
      if ($button) { try { $button.data('planc_adding', true); $button.prop('disabled', true); } catch (e) {} }
    } catch (e) { /* ignore */ }
    
    // mark a global pending flag so openCart can decide to refresh the sidebar
    try { window.planc_pending_add = true; } catch (e) {}

    // Optimistic UI: bump counts and open cart if available
    if (optimistic) {
      try {
        const $counts = $('.cart-count, .cart-contents-count');
        const prev = parseInt($counts.first().text(), 10) || 0;
        const newCount = prev + 1;
        $counts.text(newCount);
        const $badge = $('#cart-badge'); if ($badge.length) { $badge.text(newCount); $badge.removeClass('hidden'); }
        const $mobile = $('#mobile-cart-badge, .mobile-cart-badge'); if ($mobile.length) { $mobile.text(newCount); $mobile.removeClass('hidden'); }
        if (typeof window.openCart === 'function') { try { window.openCart(); } catch(e) {} }
      } catch (e) { /* ignore optimistic UI errors */ }
    }

    if ($button && $button.length) {
      // attempt a best-effort loading state change
      try { $button.addClass('loading'); const $text = $button.find('.button-text'); if ($text && $text.length) $text.text('Adding...'); } catch (e) {}
    }

    const data = { action: 'planc_add_to_cart', product_id: productId, quantity: quantity };
    if (nonce) data.nonce = nonce;

    // Optimistic sidebar insertion: create a temporary LI so the sidebar
    // shows the item immediately. We prefer button-provided metadata
    // (data attributes) and fall back to a minimal placeholder.
    let _optimisticTmpId = null;
    let _optimisticTimer = null;
    try {
      const $cartContainer = $('#cart-items-container');
      if ($cartContainer.length) {
        const $ul = $cartContainer.find('ul').first();
        // Build optimistic item markup from button data if available
        const tmpId = 'planc_tmp_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
        _optimisticTmpId = tmpId;

        const btnName = $button && ($button.data('product-name') || $button.data('name') || $button.attr('data-product-name') || $button.attr('data-name'));
        const btnThumb = $button && ($button.data('product-thumb') || $button.data('thumb') || $button.attr('data-product-thumb'));
        const btnPrice = $button && ($button.data('product-price') || $button.data('price') || $button.attr('data-product-price'));

        const nameHtml = btnName ? String(btnName) : 'Product';
        const thumbHtml = btnThumb ? ('<img class="cart-item-image w-full h-full object-cover" src="' + String(btnThumb) + '" alt="' + (btnName ? String(btnName) : '') + '">') : '';
        const priceHtml = btnPrice ? String(btnPrice) : '';

        const optimisticLi = '<li class="cart-item planc-optimistic" data-planc-temp-id="' + tmpId + '" data-product-id="' + String(productId) + '">'
          + '<div class="flex space-x-4">'
          + (thumbHtml ? ('<div class="flex-shrink-0 w-20 h-20 relative overflow-hidden rounded-lg">' + thumbHtml + '</div>') : '')
          + '<div class="flex-1 min-w-0">'
          + '<div class="flex justify-between mb-2">'
          + '<h3 class="cart-item-name pr-2"><span class="planc-optimistic-name">' + nameHtml + '</span></h3>'
          + '<p class="cart-item-total text-sm font-semibold text-gray-900 planc-optimistic-price">' + priceHtml + '</p>'
          + '</div>'
          + '<div class="flex items-center justify-between">'
          + '<div class="flex items-center space-x-1">'
          + '<span class="text-sm text-gray-500">Qty: <strong class="planc-optimistic-qty">' + String(quantity) + '</strong></span>'
          + '</div>'
          + '<div class="planc-optimistic-spinner text-sm text-gray-400">Saving...</div>'
          + '</div></div></div></li>';

        if ($ul.length) {
          $ul.prepend(optimisticLi);
        } else {
          // If UL missing, create one and insert
          const newUl = '<ul class="space-y-3">' + optimisticLi + '</ul>';
          $cartContainer.html(newUl);
        }

        // Set a stale timeout: if server fragments don't arrive in X ms, mark as failed
        const fragTimeoutMs = (window.planc_opts && window.planc_opts.fragTimeoutMs) ? window.planc_opts.fragTimeoutMs : 7000;
        _optimisticTimer = setTimeout(() => {
          try {
            const $opt = $('#cart-items-container').find('[data-planc-temp-id="' + tmpId + '"]');
            if ($opt.length) {
              $opt.addClass('planc-sync-failed');
              $opt.find('.planc-optimistic-spinner').text('Sync failed');
              // Add a retry control
              if ($opt.find('.planc-retry-sync').length === 0) {
                $opt.find('.planc-optimistic-spinner').after('<button type="button" class="planc-retry-sync ml-2 text-xs underline">Retry</button>');
                $opt.on('click', '.planc-retry-sync', function() { plancRefreshSidebar().then(() => { $opt.remove(); }).catch(() => {}); });
              }
            }
          } catch (e) {}
        }, fragTimeoutMs);
      }
    } catch (e) { /* ignore optimistic insert errors */ }

    $.ajax({
      url: ajax_url,
      type: 'POST',
      data: data,
      success: (response) => {
        try {
          if (response && response.success) {
            const fragments = (response.data && response.data.fragments) ? response.data.fragments : null;
            const addedItem = (response.data && response.data.added_item) ? response.data.added_item : null;

            // If we inserted an optimistic item earlier, update it now with server-provided data
            try {
              if (_optimisticTmpId) {
                const $opt = $('#cart-items-container').find('[data-planc-temp-id="' + _optimisticTmpId + '"]');
                if ($opt.length) {
                  if (addedItem) {
                    try {
                      if (addedItem.cart_item_key) $opt.attr('data-cart-key', addedItem.cart_item_key);
                      $opt.find('.planc-optimistic-name').text(addedItem.name || $opt.find('.planc-optimistic-name').text());
                      $opt.find('.planc-optimistic-price').html(addedItem.price_html || $opt.find('.planc-optimistic-price').html());
                      $opt.find('.planc-optimistic-qty').text(addedItem.quantity || $opt.find('.planc-optimistic-qty').text());
                      if (addedItem.thumbnail_html && $opt.find('.cart-item-image').length) {
                        try { $opt.find('.cart-item-image').replaceWith(addedItem.thumbnail_html); } catch (e) {}
                      }
                      $opt.find('.planc-optimistic-spinner').text('Saved');
                    } catch (e) {}
                  } else {
                    $opt.find('.planc-optimistic-spinner').text('Saved');
                  }
                  try { if (_optimisticTimer) { clearTimeout(_optimisticTimer); _optimisticTimer = null; } } catch (e) {}
                }
              }
            } catch (e) { /* ignore optimistic update errors */ }
            if (fragments) {
              // Apply fragments robustly (prefer jQuery replace, fallback to parent HTML, fallback to DOM replace)
              $.each(fragments, function(key, value) {
                try {
                  const $el = $(key);
                  if ($el.length) {
                    $el.replaceWith(value);
                    return;
                  }
                  const parentSelector = (typeof key === 'string' && key.indexOf(' ') !== -1) ? key.split(' ')[0] : key;
                  const $parent = $(parentSelector);
                  if ($parent.length) { $parent.html(value); return; }
                  try {
                    const node = document.querySelector(key);
                    if (node && node.parentNode) {
                      const wrapper = document.createElement('div');
                      wrapper.innerHTML = value;
                      node.parentNode.replaceChild(wrapper.firstElementChild, node);
                    }
                  } catch (err) { if (window.PLANC_DEBUG && console) console.debug('plancAddToCart fragment DOM fallback failed', err, key); }
                } catch (e) { if (window.PLANC_DEBUG && console) console.debug('plancAddToCart fragment apply failed', e, key); }
              });
              try { document.body.dispatchEvent(new CustomEvent('planc_fragments_applied', { detail: { fragments: fragments } })); } catch (e) {}
              try { window.planc_last_fragments_applied = Date.now(); } catch (e) {}
              // Remove any optimistic placeholder that was added earlier once fragments applied
              try {
                if (_optimisticTmpId) {
                  const $opt = $('#cart-items-container').find('[data-planc-temp-id="' + _optimisticTmpId + '"]');
                  if ($opt.length) {
                    // If server provided cart_item_key, try to ensure we don't clash
                    try {
                      const serverKey = (response.data && response.data.added_item && response.data.added_item.cart_item_key) ? response.data.added_item.cart_item_key : null;
                      if (serverKey) {
                        // If fragment replacement already rendered a server item with this key, just remove the optimistic
                        $opt.remove();
                      } else {
                        // Otherwise remove optimistic element to avoid duplicates (fragment likely contains canonical markup)
                        $opt.remove();
                      }
                    } catch (e) { $opt.remove(); }
                  }
                }
              } catch (e) {}
            }

            try {
              const message = (response.data && response.data.message) ? response.data.message : 'Product added to cart successfully!';
              if (window.PlancNotify && typeof window.PlancNotify.show === 'function') { window.PlancNotify.show(message, 'success'); }
            } catch (e) {}

            // Save canonical cart hash from server so future refreshes can be no-ops when unchanged
            try { if (response.data && response.data.cart_hash) { window.planc_current_cart_hash = response.data.cart_hash; } } catch (e) {}

            try { $(document.body).trigger('added_to_cart', [response.data && response.data.fragments ? response.data.fragments : null, response.data ? response.data.cart_hash : null, $button]); } catch (e) {}
          } else {
            try { $(document.body).trigger('planc_add_to_cart_failed', [response]); } catch (e) {}
          }
        } catch (e) { if (window.PLANC_DEBUG && console) console.debug('plancAddToCart success handler error', e); }
        resolve(response);
      },
      error: (xhr, _status, _error) => {
        try {
          // Rollback optimistic badge increment on error
          try {
            const $counts = $('.cart-count, .cart-contents-count');
            const curr = parseInt($counts.first().text(), 10) || 0;
            const newc = Math.max(0, curr - 1);
            $counts.text(newc);
            const $badge = $('#cart-badge'); if ($badge.length) { $badge.text(newc); $badge.toggleClass('hidden', newc === 0); }
            const $mobile = $('#mobile-cart-badge, .mobile-cart-badge'); if ($mobile.length) { $mobile.text(newc); $mobile.toggleClass('hidden', newc === 0); }
          } catch (e) {}
          try { document.body.dispatchEvent(new CustomEvent('planc_optimistic_add_failed', { detail: { productId: productId } })); } catch (e) {}
        } catch (e) {}
        // Remove optimistic placeholder on error
        try {
          if (_optimisticTmpId) {
            const $opt = $('#cart-items-container').find('[data-planc-temp-id="' + _optimisticTmpId + '"]');
            if ($opt.length) $opt.remove();
            if (_optimisticTimer) { clearTimeout(_optimisticTimer); _optimisticTimer = null; }
          }
        } catch (e) {}
        reject(xhr);
      },
      complete: function() {
        try {
          if ($button) { $button.data('planc_adding', false); $button.prop('disabled', false); $button.removeClass('loading'); const $text = $button.find('.button-text'); if ($text && $text.length) $text.text('Add to Cart'); }
        } catch (e) {}
        try { window.planc_pending_add = false; } catch (e) {}
        // Ensure optimistic timer cleared when request completes
        try { if (_optimisticTimer) { clearTimeout(_optimisticTimer); _optimisticTimer = null; } } catch (e) {}
      }
    });
  });
}

// Expose on window for legacy callers
try { window.plancAddToCart = window.plancAddToCart || plancAddToCart; } catch (e) {}

export default plancAddToCart;

// Helper: refresh sidebar cart fragment from server when needed
export function plancRefreshSidebar() {
  const $ = window.jQuery || window.$;
  if (typeof $ === 'undefined') return Promise.reject(new Error('jQuery required'));

  const ajaxCfg = window.planc_products_ajax || window.planc || {};
  const ajax_url = ajaxCfg.ajax_url || (window.location.origin + '/wp-admin/admin-ajax.php');
  const data = { action: 'planc_get_cart_sidebar' };

  return new Promise((resolve, reject) => {
    $.ajax({
      url: ajax_url,
      type: 'POST',
      data: data,
      success: function(response) {
        try {
          if (response && response.success && response.data) {
            const sidebarHtml = response.data && response.data.sidebar_ul ? response.data.sidebar_ul : null;
            const returnedHash = response.data && response.data.cart_hash ? response.data.cart_hash : null;
            const serverItems = response.data && response.data.cart_items ? response.data.cart_items : null;

            // If hash matches current displayed hash, still attempt to reconcile optimistic placeholders
            try {
              if (returnedHash && window.planc_current_cart_hash && returnedHash === window.planc_current_cart_hash) {
                // Try to remove optimistic placeholders that correspond to server items
                try {
                  if (Array.isArray(serverItems) && serverItems.length) {
                    serverItems.forEach(function(it) {
                      try {
                        var sKey = it.cart_item_key || null;
                        var pId = it.product_id || null;
                        var $opt = null;
                        if (sKey) {
                          $opt = $('#cart-items-container').find('[data-planc-temp-id]').filter(function() { return $(this).attr('data-cart-key') === sKey; });
                        }
                        if ((!$opt || $opt.length === 0) && pId) {
                          $opt = $('#cart-items-container').find('[data-planc-temp-id][data-product-id="' + pId + '"]');
                        }
                        if ($opt && $opt.length) { $opt.remove(); }
                      } catch (e) {}
                    });
                  }
                } catch (e) {}

                resolve(response.data);
                return;
              }
            } catch (e) { /* ignore */ }

            if (sidebarHtml) {
              try {
                const $target = $('#cart-items-container ul');
                if ($target.length) {
                  $target.replaceWith(sidebarHtml);
                } else {
                  // Try to append into container if UL is missing
                  const $container = $('#cart-items-container');
                  if ($container.length) { $container.html(sidebarHtml); }
                }
                try { window.planc_last_fragments_applied = Date.now(); } catch (e) {}
                try { if (returnedHash) window.planc_current_cart_hash = returnedHash; } catch (e) {}

                // Reconcile optimistic placeholders: prefer cart_item_key matching, fall back to product_id
                try {
                  if (Array.isArray(serverItems) && serverItems.length) {
                    serverItems.forEach(function(it) {
                      try {
                        var sKey = it.cart_item_key || null;
                        var pId = it.product_id || null;

                        // If server rendered element for this cart_item_key exists, remove any optimistic placeholder
                        var $serverLi = null;
                        if (sKey) { $serverLi = $('#cart-items-container').find('[data-cart-key="' + sKey + '"]'); }

                        var $opt = null;
                        // Find optimistic placeholder by matching data-cart-key or data-product-id
                        if (sKey) {
                          $opt = $('#cart-items-container').find('[data-planc-temp-id]').filter(function() { return $(this).attr('data-cart-key') === sKey; });
                        }
                        if ((!$opt || $opt.length === 0) && pId) {
                          $opt = $('#cart-items-container').find('[data-planc-temp-id][data-product-id="' + pId + '"]');
                        }

                        if ($opt && $opt.length) {
                          try {
                            // If server produced a canonical LI element, prefer to replace the optimistic
                            // placeholder with the server markup (clone HTML) so insertion order stays
                            // where the user saw the optimistic item and we avoid flicker.
                            if ($serverLi && $serverLi.length) {
                              try {
                                var serverHtml = $serverLi.prop('outerHTML');
                                // Replace optimistic placeholder with server HTML
                                $opt.each(function() { $(this).replaceWith(serverHtml); });
                                // Remove the original server LI to avoid duplicates (we cloned it into place)
                                $serverLi.remove();
                              } catch (e) {
                                // Fallback: if replacement fails, remove optimistic placeholder
                                $opt.remove();
                              }
                            } else {
                              // Server did not output a LI with cart_key; if server provided inline
                              // HTML fragments like thumbnail/price, update the optimistic node in-place
                              try {
                                if (it.thumbnail_html) {
                                  $opt.find('.cart-item-image').length ? $opt.find('.cart-item-image').replaceWith(it.thumbnail_html) : null;
                                }
                                if (it.line_total) {
                                  $opt.find('.planc-optimistic-price').html(it.line_total);
                                }
                                // Remove optimistic status marker
                                $opt.removeClass('planc-optimistic planc-sync-failed');
                                $opt.find('.planc-optimistic-spinner').remove();
                              } catch (e) {
                                $opt.remove();
                              }
                            }
                          } catch (e) { try { $opt.remove(); } catch (ee) {} }
                        }
                      } catch (e) {}
                    });
                  }
                } catch (e) {}

                resolve(response.data);
                return;
              } catch (e) { if (window.PLANC_DEBUG && console) console.debug('plancRefreshSidebar apply failed', e); }
            }
          }
          resolve(null);
        } catch (e) { resolve(null); }
      },
      error: function(err) { reject(err); }
    });
  });
}

try { window.plancRefreshSidebar = window.plancRefreshSidebar || plancRefreshSidebar; } catch (e) {}
